import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Clock, Loader2, Lock, Trophy } from 'lucide-react';
import useActiveScope from '@/lib/useActiveScope';
import { createMock, fetchMockConfig, fetchMocks, submitMock } from '@/lib/mockExams';

const fmt = (secs) => {
  const s = Math.max(0, secs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h ? `${h}:` : ''}${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

/**
 * Mock Exam Board — the rehearsal that removes exam fear.
 *
 * Chapter mocks are always open; the full-subject paper unlocks once every
 * chapter of that subject is done, and the number of full papers a student gets
 * comes from their plan (the server decides, we only display it).
 */
export default function MockExamPage() {
  const { subject, topic } = useActiveScope();
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [scope, setScope] = useState('chapter');
  const [chapter, setChapter] = useState(topic || '');
  const [marks, setMarks] = useState(40);
  const [minutes, setMinutes] = useState(60);
  const [paper, setPaper] = useState(null);
  const [answers, setAnswers] = useState({});
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const startedRef = useRef(0);

  const load = useCallback(async () => {
    if (!subject) return;
    try {
      const [cfg, list] = await Promise.all([fetchMockConfig(subject), fetchMocks(subject)]);
      setConfig(cfg);
      setHistory(list.mocks || []);
      setMinutes(cfg.suggested_minutes?.[String(marks)] || 60);
    } catch (e) { toast.error(e?.message || 'The mock exam board is unavailable right now'); }
  }, [subject, marks]);

  useEffect(() => { load(); }, [load]);

  const submit = useCallback(async (auto = false) => {
    if (!paper || busy) return;
    setBusy(true);
    try {
      const res = await submitMock(paper.mock_id, {
        answers,
        seconds_taken: Math.round((Date.now() - startedRef.current) / 1000),
      });
      setPaper(res);
      toast.success(auto ? "Time's up — paper submitted" : 'Paper submitted');
      fetchMocks(subject).then(d => setHistory(d.mocks || [])).catch(() => {});
    } catch (e) { toast.error(e?.message || 'Could not submit the paper'); }
    setBusy(false);
  }, [paper, answers, busy, subject]);

  useEffect(() => {
    if (!paper || paper.status !== 'in_progress') return;
    const tick = () => {
      const remaining = Math.round((new Date(paper.deadline_at).getTime() - Date.now()) / 1000);
      setLeft(remaining);
      if (remaining <= 0) submit(true);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [paper, submit]);

  const start = async () => {
    setBusy(true);
    try {
      const res = await createMock({
        subject,
        scope,
        chapters: scope === 'chapter' ? [chapter] : [],
        total_marks: marks,
        duration_minutes: minutes,
      });
      startedRef.current = Date.now();
      setAnswers({});
      setPaper(res);
    } catch (e) { toast.error(e?.message || 'Could not build the paper'); }
    setBusy(false);
  };

  const answered = useMemo(() => Object.keys(answers).length, [answers]);

  if (!subject) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="mock-no-scope">
        <ClipboardCheck className="w-10 h-10 text-rose-300 mx-auto" />
        <p className="font-heading text-base text-slate-800 mt-3">Pick a subject first</p>
        <p className="font-body text-sm text-slate-500 mt-1">Choose it in the bar at the top and your mock board appears here.</p>
      </div>
    );
  }

  /* ---------- running / finished paper ---------- */
  if (paper) {
    const done = paper.status === 'submitted';
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="mock-paper">
        <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-white/90 backdrop-blur border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-heading text-sm font-bold text-slate-900 truncate">{paper.title}</p>
            <p className="font-body text-[11px] text-slate-500">
              {paper.question_count} questions · {paper.total_marks} marks · {paper.duration_minutes} min
            </p>
          </div>
          {done ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-[12px] font-heading" data-testid="mock-score">
              <Trophy className="w-3.5 h-3.5" /> {paper.score} / {paper.total_marks} · {paper.percent}%
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-heading tabular-nums ${
              left < 300 ? 'bg-rose-50 text-rose-700' : 'bg-slate-900 text-white'}`} data-testid="mock-timer">
              <Clock className="w-3.5 h-3.5" /> {fmt(left)}
            </span>
          )}
        </div>

        {done && (
          <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-4" data-testid="mock-result">
            <p className="font-body text-sm text-slate-700">{paper.verdict}</p>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {(paper.chapter_breakdown || []).map(r => (
                <div key={r.chapter} className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-2"
                  data-testid={`mock-breakdown-${r.chapter}`}>
                  <p className="font-heading text-[12px] text-slate-800">{r.chapter}</p>
                  <p className="font-body text-[11px] text-slate-500">{r.correct} of {r.total} right · {r.percent}%</p>
                </div>
              ))}
            </div>
            <button onClick={() => { setPaper(null); load(); }} data-testid="mock-back"
              className="mt-3 text-[12px] font-heading text-indigo-600 hover:underline">Back to the mock board</button>
          </div>
        )}

        <div className="mt-4 space-y-3">
          {(paper.questions || []).map((q, i) => {
            const chosen = done ? q.chosen_index : answers[q.question_id];
            return (
              <div key={q.question_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`mock-q-${i}`}>
                <p className="font-heading text-[13px] text-slate-900">
                  <span className="text-slate-400 mr-1.5">Q{i + 1}.</span>{q.question}
                </p>
                <div className="mt-2.5 space-y-1.5">
                  {q.options.map((opt, oi) => {
                    const right = done && oi === q.correct_index;
                    const wrong = done && chosen === oi && oi !== q.correct_index;
                    return (
                      <button key={oi} disabled={done}
                        onClick={() => setAnswers(a => ({ ...a, [q.question_id]: oi }))}
                        data-testid={`mock-q-${i}-opt-${oi}`}
                        className={`w-full text-left rounded-xl border px-3 py-2 font-body text-[13px] transition-colors ${
                          right ? 'border-emerald-400 bg-emerald-50 text-emerald-900'
                          : wrong ? 'border-rose-300 bg-rose-50 text-rose-900'
                          : chosen === oi ? 'border-indigo-400 bg-indigo-50 text-indigo-900'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-200'}`}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {done && q.explanation && (
                  <p className="mt-2 font-body text-[11px] text-slate-500">Why: {q.explanation}</p>
                )}
              </div>
            );
          })}
        </div>

        {(paper.paper_questions || []).length > 0 && (
          <div className="mt-4 rounded-3xl border border-amber-200 bg-amber-50/60 p-4" data-testid="mock-paper-questions">
            <p className="font-heading text-[13px] font-bold text-amber-900">Straight from real papers (not scored)</p>
            <p className="font-body text-[11px] text-amber-800/80 mt-0.5">
              These appeared in actual exams — write the answers in your notebook, then check them with your teacher or Study Buddy.
            </p>
            <ul className="mt-2 space-y-1.5">
              {paper.paper_questions.map((q, i) => (
                <li key={i} className="font-body text-[12px] text-amber-900">
                  • {q.question} <span className="text-amber-700/70">({q.appeared_in} paper{q.appeared_in > 1 ? 's' : ''})</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!done && (
          <div className="sticky bottom-0 -mx-4 mt-5 px-4 py-3 bg-white/90 backdrop-blur border-t border-slate-100 flex items-center justify-between gap-3">
            <p className="font-body text-[12px] text-slate-500">{answered} of {paper.question_count} answered</p>
            <button onClick={() => submit(false)} disabled={busy} data-testid="mock-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white text-[13px] font-heading disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit paper
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ---------- board ---------- */
  const unlocked = config?.subject_mock_unlocked;
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="mock-exam-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Mock Exam</h1>
        <p className="font-body text-sm text-slate-500 mt-1">
          {subject} · a timed rehearsal, marked the way a real paper is — so the real one holds no surprises.
        </p>
      </div>

      {!config ? (
        <p className="flex items-center gap-2 text-slate-400 font-body text-sm" data-testid="mock-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Opening the board…
        </p>
      ) : (
        <>
          <div className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4" data-testid="mock-setup">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setScope('chapter')} data-testid="mock-scope-chapter"
                className={`px-3.5 py-2 rounded-full text-[12px] font-heading border transition-colors ${
                  scope === 'chapter' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
                One chapter
              </button>
              <button onClick={() => setScope('subject')} data-testid="mock-scope-subject"
                className={`px-3.5 py-2 rounded-full text-[12px] font-heading border transition-colors inline-flex items-center gap-1.5 ${
                  scope === 'subject' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
                {!unlocked && <Lock className="w-3 h-3" />} Full portion
              </button>
            </div>

            {scope === 'chapter' ? (
              <div>
                <p className="font-heading text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Chapter</p>
                <select value={chapter} onChange={e => setChapter(e.target.value)} data-testid="mock-chapter-select"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 font-body text-[13px] text-slate-700 bg-white">
                  <option value="">Pick a chapter</option>
                  {(config.chapters || []).map(c => (
                    <option key={c.name} value={c.name}>{c.name}{c.completed ? ' ✓' : ''}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className={`rounded-2xl border p-3 ${unlocked ? 'border-emerald-200 bg-emerald-50/60' : 'border-amber-200 bg-amber-50/60'}`}
                data-testid="mock-subject-status">
                {unlocked ? (
                  <p className="font-body text-[12px] text-emerald-900">
                    Every chapter of {subject} is done — the full-portion paper is open. Your plan includes{' '}
                    {config.allowance_per_subject} per subject ({config.mocks_remaining} left).
                  </p>
                ) : (
                  <p className="font-body text-[12px] text-amber-900 flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    {(config.chapters_pending || []).length} chapter(s) still to finish: {(config.chapters_pending || []).slice(0, 4).join(', ')}
                    {(config.chapters_pending || []).length > 4 ? '…' : ''}. A chapter mock is open to you right now.
                  </p>
                )}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="font-heading text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Total marks</p>
                <div className="flex flex-wrap gap-1.5">
                  {(config.marks_choices || []).map(m => (
                    <button key={m} data-testid={`mock-marks-${m}`}
                      onClick={() => { setMarks(m); setMinutes(config.suggested_minutes?.[String(m)] || 60); }}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-heading border ${
                        marks === m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-heading text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">
                  Duration · {minutes} min
                </p>
                <input type="range" min={config.duration_band?.min || 30} max={config.duration_band?.max || 180} step={5}
                  value={minutes} onChange={e => setMinutes(Number(e.target.value))}
                  className="w-full accent-indigo-600" data-testid="mock-duration" />
                <p className="font-body text-[11px] text-slate-400 mt-1">
                  {config.duration_band?.min}–{config.duration_band?.max} minutes. We suggest {config.suggested_minutes?.[String(marks)]} for {marks} marks.
                </p>
              </div>
            </div>

            <p className="font-body text-[11px] text-slate-400">{config.note}</p>

            <button onClick={start} data-testid="mock-start"
              disabled={busy || (scope === 'chapter' ? !chapter : !unlocked || config.mocks_remaining <= 0)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white font-heading text-sm disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
              Start the {scope === 'chapter' ? 'chapter' : 'full-portion'} mock
            </button>
          </div>

          {history.length > 0 && (
            <div className="space-y-2" data-testid="mock-history">
              <p className="font-heading text-xs uppercase tracking-widest text-slate-400">Your papers</p>
              {history.map(m => (
                <div key={m.mock_id} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-3"
                  data-testid={`mock-history-${m.mock_id}`}>
                  <div className="min-w-0">
                    <p className="font-heading text-[13px] text-slate-800 truncate">{m.title}</p>
                    <p className="font-body text-[11px] text-slate-500">
                      {m.question_count} questions · {m.total_marks} marks · {m.duration_minutes} min
                    </p>
                  </div>
                  {m.status === 'submitted' ? (
                    <span className={`text-[12px] font-heading px-3 py-1 rounded-full ${m.percent >= 75 ? 'bg-emerald-50 text-emerald-700' : m.passed ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'}`}>
                      {m.percent}%
                    </span>
                  ) : (
                    <span className="text-[11px] font-heading text-slate-400">in progress</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
